import { Component } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import { AngularMaterialModule } from 'src/app/material.module';
// Direct path, not the @lib/modules barrel — that barrel also re-exports table.module, whose
// table.component.ts has a pre-existing circular import that breaks Karma's webpack bundling.
import { ValueFieldModule } from '@lib/modules/value-field/value-field.module';
import { BaseComponent } from '@lib/components';
import { ApiResponseError } from '@lib/interfaces';
import { INeoCashOutVoucherDetails, NEO_CASH_OUT_PANEL_MIN_WIDTH_PX, NeoCashOutVoucherErrorCode, VOUCHER_STATUS } from '@lib/interfaces/card-less.interface';
import { CardLessService } from '@lib/services/card-less/card-less.service';
import { SidePanelService } from '@lib/services';
import { VoucherDetailsComponent } from '../voucher-details/voucher-details.component';

// NEOIQ-2700: per backend, the voucher code length is configurable (PMD-side) — not fixed. BE
// confirmed the default is 9 digits, matching Figma. Update this single constant if/when that
// configuration is surfaced to the frontend (e.g. via a tenant setting or the voucher-generation
// response) instead of a hardcoded value. The box-grouping CSS (neo-cash-out.shared.scss) scales
// to any length automatically — it doesn't assume 9 specifically.
const VOUCHER_CODE_LENGTH = 9;

interface IErrorBanner {
    title: string;
    message: string;
}

// Keyed by NeoCashOutVoucherErrorCode — see its comment in card-less.interface.ts for which of
// these are confirmed against a real error response vs. still-inferred placeholders. Anything that
// doesn't match a known code falls through to a generic snackbar in handleError() below.
const ERROR_BANNERS: Record<NeoCashOutVoucherErrorCode, IErrorBanner> = {
    [NeoCashOutVoucherErrorCode.EXPIRED]: {
        title: 'Voucher expired',
        message: "This voucher is no longer valid. The money has been returned to the customer's wallet.",
    },
    [NeoCashOutVoucherErrorCode.ALREADY_REDEEMED]: {
        title: 'Voucher already redeemed',
        message: "This voucher has already been used and can't be redeemed again.",
    },
    [NeoCashOutVoucherErrorCode.NOT_FOUND]: {
        title: 'Voucher code does not exist',
        message: 'Confirm the code with the customer and enter it again.',
    },
    [NeoCashOutVoucherErrorCode.CANCELLED]: {
        title: 'Voucher cancelled',
        message: "This voucher can't be used. Ask the customer to create a new voucher in the app.",
    },
};

// A successful lookup can still return a voucher that's past its redeemable state (e.g. the agent
// re-enters a code that was already redeemed, or the customer cancelled it) — only NEW is
// redeemable. Catching that here avoids sending the agent to the Approve & Pay Cash screen just to
// have the redeem call reject with "Voucher cannot be redeemed: it is REDEEMED." (NEOIQ-2700).
const NON_REDEEMABLE_STATUS_BANNERS: Partial<Record<VOUCHER_STATUS, IErrorBanner>> = {
    [VOUCHER_STATUS.REDEEMED]: ERROR_BANNERS[NeoCashOutVoucherErrorCode.ALREADY_REDEEMED],
    [VOUCHER_STATUS.EXPIRED]: ERROR_BANNERS[NeoCashOutVoucherErrorCode.EXPIRED],
    [VOUCHER_STATUS.CANCELED]: ERROR_BANNERS[NeoCashOutVoucherErrorCode.CANCELLED],
};

// Figma (node 8456:125179) shows a 5th banner distinct from the four backend-driven ones above:
// Iraqi keyboards can type Arabic-Indic digits (٠-٩ / U+0660-0669, or the Extended Arabic-Indic
// U+06F0-06F9 used by Urdu/Persian layouts) which look like numbers but aren't '0'-'9' — the lookup
// would silently fail against them. Caught client-side, reactively, as soon as one is typed, rather
// than waiting for the agent to hit Continue.
const ARABIC_INDIC_DIGIT_PATTERN = /[٠-٩۰-۹]/;
const KEYBOARD_LANGUAGE_BANNER: IErrorBanner = {
    title: 'Switch to English keyboard',
    message: 'Enter the voucher code using English numbers (0–9).',
};

@Component({
    selector: 'app-enter-voucher-code',
    standalone: true,
    templateUrl: './enter-voucher-code.component.html',
    styleUrls: ['./enter-voucher-code.component.scss'],
    imports: [AngularMaterialModule, ReactiveFormsModule, ValueFieldModule],
})
export class EnterVoucherCodeComponent extends BaseComponent {
    agentAccountId: string;

    protected readonly voucherCodeLength = VOUCHER_CODE_LENGTH;
    protected voucherCodeControl = new FormControl('');
    protected loading = false;
    protected errorBanner: IErrorBanner | null = null;

    constructor(private readonly dialogRef: MatDialogRef<EnterVoucherCodeComponent>, private readonly sidePanel: SidePanelService, private readonly cardLessService: CardLessService, private readonly snackbar: MatSnackBar) {
        super();

        this.addSubscription(
            this.voucherCodeControl.valueChanges.subscribe((value) => {
                if (ARABIC_INDIC_DIGIT_PATTERN.test(value ?? '')) {
                    this.errorBanner = KEYBOARD_LANGUAGE_BANNER;
                } else if (this.errorBanner === KEYBOARD_LANGUAGE_BANNER) {
                    this.errorBanner = null;
                }
            }),
        );
    }

    protected get isCodeComplete(): boolean {
        return (this.voucherCodeControl.value ?? '').length === VOUCHER_CODE_LENGTH;
    }

    protected clearAll(): void {
        this.voucherCodeControl.reset('');
        this.errorBanner = null;
    }

    protected continue(): void {
        if (!this.isCodeComplete || this.errorBanner) return;

        const voucherCode = this.voucherCodeControl.value!;
        this.errorBanner = null;
        this.loading = true;

        this.addSubscription(
            this.cardLessService
                .neoCashOutInquiry(voucherCode)
                .pipe(finalize(() => (this.loading = false)))
                .subscribe({
                    next: (voucherDetails: INeoCashOutVoucherDetails) => {
                        const banner = NON_REDEEMABLE_STATUS_BANNERS[voucherDetails.voucher_status];
                        if (banner) {
                            this.errorBanner = banner;
                            return;
                        }

                        this.dialogRef.close();
                        const dialogRef = this.sidePanel.open(VoucherDetailsComponent, {}, NEO_CASH_OUT_PANEL_MIN_WIDTH_PX);
                        dialogRef.componentInstance.voucherDetails = voucherDetails;
                        dialogRef.componentInstance.agentAccountId = this.agentAccountId;
                    },
                    error: (error: ApiResponseError) => this.handleError(error),
                }),
        );
    }

    private handleError(error: ApiResponseError): void {
        // RFC7807 problem responses carry a stable `type` URL (e.g. ".../problems/not-found") —
        // matched on its last path segment rather than the free-text `detail` sentence ("Voucher
        // not found"), which is for display, not for branching on.
        const problemType = error.error?.type?.split('/').pop() as NeoCashOutVoucherErrorCode | undefined;
        const banner = problemType ? ERROR_BANNERS[problemType] : undefined;

        if (banner) {
            this.errorBanner = banner;
            return;
        }

        // Unrecognized error shape — surface it as a snackbar rather than guessing which of the
        // four known voucher states it maps to.
        this.createApiErrorSnackBar(this.snackbar, error);
    }

    protected cancel(): void {
        this.dialogRef.close();
    }
}
